import { useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiAlertCircle,
  FiExternalLink,
  FiCheck,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import {
  createGenerator,
  updateGenerator,
  deleteGenerator,
} from "../../services/generatorService";
import TestEndpointModal from "./TestEndpointModal";

const GeneratorsTable = ({ generators, onGeneratorsChange }) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGenerator, setEditingGenerator] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    endpoint: "",
    authType: "none",
    authCredential: "",
    concurrencyLimit: 20,
  });

  // Test endpoint state
  const [testingGenerator, setTestingGenerator] = useState(null);
  const [showPostCreatePrompt, setShowPostCreatePrompt] = useState(null); // generator object

  const loading = !generators;

  const handleAdd = () => {
    setEditingGenerator(null);
    setFormData({
      name: "",
      endpoint: "",
      authType: "none",
      authCredential: "",
      concurrencyLimit: 20,
    });
    setError(null);
    setIsModalOpen(true);
  };

  const handleEdit = (generator) => {
    setEditingGenerator(generator);
    setFormData({
      name: generator.name,
      endpoint: generator.endpoint_url,
      authType: generator.auth_type || "none",
      authCredential: "", // Always empty for security - user must re-enter to update
      concurrencyLimit: generator.concurrency_limit || 20,
    });
    setError(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this generator?")) {
      const { error: deleteError } = await deleteGenerator(id);

      if (deleteError) {
        alert("Failed to delete generator. Please try again.");
        console.error(deleteError);
      } else {
        // Refresh the list
        onGeneratorsChange();
      }
    }
  };

  const handleTest = (generator) => {
    setTestingGenerator(generator);
  };

  const handleTestClose = () => {
    setTestingGenerator(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingGenerator) {
        // Update existing generator
        const { error: updateError } = await updateGenerator(
          editingGenerator.id,
          {
            name: formData.name,
            endpoint_url: formData.endpoint,
            auth_type: formData.authType,
            auth_credential: formData.authCredential,
            concurrency_limit: formData.concurrencyLimit,
          }
        );

        if (updateError) {
          throw updateError;
        }
      } else {
        // Create new generator
        if (!user) {
          alert("You must be logged in to create a generator.");
          setSubmitting(false);
          return;
        }

        const { data: newGenerator, error: createError } =
          await createGenerator(
            {
              name: formData.name,
              endpoint_url: formData.endpoint,
              auth_type: formData.authType,
              auth_credential: formData.authCredential,
              concurrency_limit: formData.concurrencyLimit,
            },
            user.id
          );

        if (createError) {
          throw createError;
        }

        // Show post-creation test prompt
        if (newGenerator) {
          setShowPostCreatePrompt(newGenerator);
        }
      }

      // Refresh the list
      await onGeneratorsChange();

      // Close modal and reset form
      setIsModalOpen(false);
      setFormData({
        name: "",
        endpoint: "",
        authType: "none",
        authCredential: "",
        concurrencyLimit: 20,
      });
    } catch (err) {
      setError("Failed to save generator. Please try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="rounded-2xl border border-gray-200 bg-white shadow-soft">
        <div className="border-b border-gray-100 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Generators
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                Manage content generator endpoints.{" "}
                <Link
                  to="/generator-api-interface"
                  className="inline-flex items-center gap-1 font-medium text-primary-600 hover:text-primary-700 hover:underline"
                >
                  View API Interface Spec
                  <FiExternalLink className="size-3" />
                </Link>
              </p>
            </div>

            <button
              onClick={handleAdd}
              className="flex items-center space-x-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <FiPlus className="size-4" />
              <span>Add Generator</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-8 mt-6 flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-4">
            <FiAlertCircle className="size-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="px-8 py-12 text-center">
            <div className="inline-block size-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-500">Loading generators...</p>
          </div>
        ) : generators.length === 0 ? (
          /* Empty State */
          <div className="px-8 py-12 text-center">
            <p className="text-gray-500">
              No generators found. Add your first generator to get started.
            </p>
          </div>
        ) : (
          /* Table */
          <div className="max-h-[400px] overflow-auto rounded-b-2xl">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Endpoint URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Authentication
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Concurrency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {generators.map((generator) => (
                  <tr key={generator.id} className="hover:bg-gray-50">
                    <td className="min-w-[150px] max-w-[240px] px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="whitespace-normal break-words">
                        {generator.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-md truncate">
                        {generator.endpoint_url}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {generator.auth_type === "none" ? (
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                          None
                        </span>
                      ) : generator.auth_type === "bearer" ? (
                        <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                          Bearer Token
                        </span>
                      ) : generator.auth_type === "x-api-key" ? (
                        <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">
                          X-API-Key
                        </span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                          Unknown
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                        {generator.concurrency_limit || 20}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleTest(generator)}
                          title="Test Endpoint"
                          className="rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition-all duration-200 hover:border-primary-300 hover:bg-primary-100"
                        >
                          Test
                        </button>
                        <button
                          onClick={() => handleEdit(generator)}
                          title="Edit Generator"
                          className="rounded-lg p-2 text-primary-600 transition-all duration-200 hover:bg-primary-50 hover:text-primary-700"
                        >
                          <FiEdit2 className="size-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(generator.id)}
                          title="Delete Generator"
                          className="rounded-lg p-2 text-red-600 transition-all duration-200 hover:bg-red-50 hover:text-red-700"
                        >
                          <FiTrash2 className="size-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generator Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-large">
            <button
              onClick={() => {
                setIsModalOpen(false);
                setError(null);
              }}
              className="absolute right-4 top-4 rounded-lg text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600"
            >
              <FiX className="size-6" />
            </button>

            <h3 className="mb-2 text-2xl font-semibold text-gray-900">
              {editingGenerator ? "Edit Generator" : "Add Generator"}
            </h3>

            {/* API Interface Info */}
            {!editingGenerator && (
              <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-100">
                  <FiExternalLink className="size-4 text-primary-600" />
                </div>
                <div className="text-sm">
                  <p className="font-medium text-primary-900">
                    Your endpoint must follow our API interface
                  </p>
                  <Link
                    to="/generator-api-interface"
                    className="mt-1 inline-flex items-center gap-1 text-primary-700 hover:text-primary-800 hover:underline"
                  >
                    View Interface Specification
                    <FiExternalLink className="size-3" />
                  </Link>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-3">
                <FiAlertCircle className="size-5 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-base transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                  placeholder="Your Generator Name"
                />
              </div>

              <div>
                <label
                  htmlFor="endpoint"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Endpoint URL
                </label>
                <input
                  id="endpoint"
                  type="url"
                  required
                  autoComplete="url"
                  value={formData.endpoint}
                  onChange={(e) =>
                    setFormData({ ...formData, endpoint: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-base transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                  placeholder="https://your-generator-endpoint.com/v1/generate"
                />
              </div>

              <div>
                <label
                  htmlFor="authType"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Authentication Type
                </label>
                <select
                  id="authType"
                  value={formData.authType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      authType: e.target.value,
                      authCredential: "",
                    })
                  }
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-base transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  <option value="none">None (No Authentication)</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="x-api-key">X-API-Key</option>
                </select>
              </div>

              {formData.authType !== "none" && (
                <div>
                  <label
                    htmlFor="authCredential"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    {formData.authType === "bearer"
                      ? "Bearer Token"
                      : "API Key"}
                    {editingGenerator && (
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        - Leave empty to keep current credential
                      </span>
                    )}
                  </label>
                  <input
                    id="authCredential"
                    type="password"
                    autoComplete="new-password"
                    value={formData.authCredential}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        authCredential: e.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-base transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                    placeholder={
                      editingGenerator
                        ? `Enter new ${formData.authType === "bearer" ? "token" : "API key"} to update`
                        : `Enter ${formData.authType === "bearer" ? "bearer token" : "API key"}`
                    }
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="concurrencyLimit"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Concurrency Limit
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    - Max parallel generation requests (1-100)
                  </span>
                </label>
                <input
                  id="concurrencyLimit"
                  type="number"
                  min="1"
                  max="100"
                  required
                  value={formData.concurrencyLimit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      concurrencyLimit: Math.min(
                        100,
                        Math.max(1, parseInt(e.target.value) || 20)
                      ),
                    })
                  }
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-base transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                  placeholder="20"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Controls how many generation requests run in parallel during
                  benchmarking. Higher values may speed up runs but could
                  overwhelm your endpoint.
                </p>
              </div>

              <div className="flex space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setError(null);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-base font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-lg bg-primary-600 px-4 py-3 text-base font-semibold text-white transition-all duration-200 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting
                    ? "Saving..."
                    : editingGenerator
                      ? "Update"
                      : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Post-creation test prompt */}
      {showPostCreatePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-large">
            <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-green-100">
              <FiCheck className="size-6 text-green-600" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-gray-900">
              Generator Created!
            </h3>
            <p className="mb-6 text-sm text-gray-500">
              Would you like to test your endpoint to make sure everything is
              set up correctly?
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowPostCreatePrompt(null)}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-base font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50"
              >
                Skip
              </button>
              <button
                onClick={() => {
                  const gen = showPostCreatePrompt;
                  setShowPostCreatePrompt(null);
                  handleTest(gen);
                }}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 text-base font-semibold text-white transition-all duration-200 hover:bg-primary-700"
              >
                Test Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Endpoint Modal */}
      {testingGenerator && (
        <TestEndpointModal
          generator={testingGenerator}
          onClose={() => {
            handleTestClose();
          }}
          onEditGenerator={(gen) => {
            handleTestClose();
            handleEdit(gen);
          }}
        />
      )}
    </>
  );
};

GeneratorsTable.propTypes = {
  generators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      endpoint_url: PropTypes.string,
      auth_type: PropTypes.oneOf(["none", "bearer", "x-api-key"]),
      auth_credential: PropTypes.string,
      concurrency_limit: PropTypes.number,
    })
  ).isRequired,
  onGeneratorsChange: PropTypes.func.isRequired,
};

export default GeneratorsTable;
